<script setup lang="ts">
import type { Game, GameClickEventEmit, GameListProps, GameTilePosition } from '@game/types'
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, useDevice, useGameImpressions, useI18n, useImpressionObserver, useGameAssets, useNuxtApp, useRoute, useTemplateRef, useTileVisibility, useTracking, useUserInfo, watch, whenever, type Ref, useResizeObserver } from '#imports'

const { t } = useI18n()
const { isVideoTile } = useGameAssets()
// User-agent based so SSR and client agree (eager set is part of SSR markup)
const { isMobile } = useDevice()

const props = withDefaults(defineProps<GameListProps>(), {
    showAllButton: false,
    forceShowAllButton: false,
    showNewGames: true,
    trimDanglingTiles: false,
    hasSidebar: false,
    videoEnabled: true,
    skeletons: 15,
})

const emit = defineEmits<GameClickEventEmit & {
    'load-more-games': [category: string]
}>()

const currentGamesCount = computed(() => props.numberOfGames || props.games?.length)
const gameListBarWidth = computed(() => {
    if (!currentGamesCount.value || !props.totalGames) return 0
    return (currentGamesCount.value / props.totalGames) * 100
})

const firstNewGame: Ref<number[]> = ref([0])

const getGameSetIndex = (index: number) => {
    if (!firstNewGame.value?.length) return 0

    // Find which set this game belongs to by finding the last firstNewGame index that is <= current index
    const setStartIndex = firstNewGame.value.findLast(startIndex => startIndex <= index) || 0

    // Return the index within the current set
    return index - setStartIndex
}

if (import.meta.client) {
    whenever(() => props.games, (newGames) => {
        firstNewGame.value.push(newGames.length)
    })
}

const { trackEvent } = useTracking()
const loadMoreClicks = ref(0)

function loadMoreGames(gamesCategory: string) {
    if (!currentGamesCount.value || !props.totalGames) return

    if (currentGamesCount.value < props.totalGames) {
        emit('load-more-games', props.category)
        loadMoreClicks.value += 1

        void trackEvent({
            event_name: `load_more_games`,
            event_type: 'Click',
            meta_data: [{
                category: 'Games',
                gamesCategory,
                totalClicks: loadMoreClicks.value,
                gamesLoaded: currentGamesCount.value,
            }],
            log_entry: true
        })
    }
}

// Check if all games are loaded - used to disable row limiting when complete
const skeletonCount = computed(() => props.skeletons || 0)

const maxVisibleTiles = computed(() => {
    if (props.numberOfGames) return props.numberOfGames
    if (!props.games?.length && props.skeletons) return props.skeletons
    if (!props.trimDanglingTiles) return Infinity

    const maxCols = props.maxColumns || 5

    return maxCols * 3
})

const visibleGamesCount = computed(() => {
    const countGames = props.games?.length || 0

    if (!countGames) return Math.min(skeletonCount.value, maxVisibleTiles.value)

    return Math.min(countGames, maxVisibleTiles.value)
})

// Set by the tile's own `@loaded` emit. Drives the skeleton fade, unrelated to
// the viewport sets below.
const loadedTiles = reactive(new Set<number>())

const { mounted: visibleTiles, active: mediaTiles, setVisible: setTileVisible, observerOptions } = useTileVisibility()

// Eager only during SSR + hydration so SSR/client markup agree (no hydration
// mismatch); client-side navs after hydration lazy-load via IntersectionObserver.
const nuxtApp = useNuxtApp()
const eagerTiles = computed(() => (import.meta.server || nuxtApp.isHydrating) && !!props.games?.length)

const eagerTileCount = computed(() => props.eagerTileCount ?? (isMobile ? 4 : 8))

function isEagerTile(index: number) {
    return eagerTiles.value && index < eagerTileCount.value
}

watch([() => props.games, eagerTileCount], () => {
    if (!eagerTiles.value) return

    for (let i = 0; i < (props.games?.length ?? 0); i++) {
        if (!isEagerTile(i)) continue

        loadedTiles.add(i)
        setTileVisible(i, true)
    }
}, { immediate: true })

let observer: IntersectionObserver | null = null

function observeTileSlots() {
    if (!gameListRef.value) return

    const slots = gameListRef.value.querySelectorAll<HTMLElement>('[data-tile-index]')

    for (const slot of slots) {
        if (observer) {
            // observe() is idempotent per target, so keeping every slot observed
            // (instead of unobserving on first hit) lets videos pause on exit.
            observer.observe(slot)
        }
        else {
            // No IntersectionObserver support: mount everything up front.
            setTileVisible(Number(slot.dataset.tileIndex), true)
        }
    }
}

const gameListRef = useTemplateRef('gameList')
const currentColumns = ref<number | null>(null)
let pendingColumnsFrame: number | null = null

function updateColumns() {
    if (!gameListRef.value) {
        currentColumns.value = null
        return
    }

    currentColumns.value = getComputedStyle(gameListRef.value).gridTemplateColumns.split(' ').length
}

// Nominal grid coordinates of a tile. `index` is the exact zero-based position in
// the games array; row/col are one-based and derived from the live column count
// (video tiles span two columns, so these are the layout's nominal position, not a
// pixel-exact cell).
function tilePosition(index: number): GameTilePosition {
    const cols = currentColumns.value || props.maxColumns || 1

    return { row: Math.floor(index / cols) + 1, col: (index % cols) + 1, index }
}

const { data: userInfo } = useUserInfo()
const impressions = useGameImpressions()

const trackingUrl = useRoute().path

const { observe: observeImpressions } = useImpressionObserver('[data-tile-index]', (el) => {
    const index = Number(el.dataset.tileIndex)
    const game = props.games?.[index]
    // Skeleton slot — the keyed remount re-observes it once the game arrives.
    if (!game) return

    impressions.record({
        origin: props.trackingOrigin,
        listSlug: props.trackingListSlug ?? props.category,
        gameSlug: game.slug,
        url: trackingUrl,
        ...tilePosition(index),
        userId: userInfo.value?.id,
    })
})

function handleObserverEntries(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
        setTileVisible(Number((entry.target as HTMLElement).dataset.tileIndex), entry.isIntersecting)
    }
}

onMounted(() => {
    // Use a default intersection observer, since the vueuse observer is too aggressive with the cleanup, causing some tiles not to be loaded
    if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(handleObserverEntries, observerOptions)
    }

    updateColumns()
    observeTileSlots()

    if (impressions.enabled) observeImpressions(gameListRef.value)
})

onBeforeUnmount(() => {
    observer?.disconnect()
    observer = null

    if (pendingColumnsFrame !== null) {
        cancelAnimationFrame(pendingColumnsFrame)
        pendingColumnsFrame = null
    }
})

useResizeObserver(gameListRef, () => {
    if (pendingColumnsFrame !== null) return

    pendingColumnsFrame = requestAnimationFrame(() => {
        pendingColumnsFrame = null
        updateColumns()
    })
})

function getLastCompleteRowEnd(cols: number, count: number) {
    let slots = 0
    let lastCompleteRowEnd = 0

    for (let i = 0; i < count; i++) {
        const game = props.games?.[i]
        const isVideo = isVideoTile(game, props.videoEnabled)

        slots += isVideo ? 2 : 1

        if (slots % cols === 0) {
            lastCompleteRowEnd = i + 1
        }
    }

    return lastCompleteRowEnd || count
}

const trimmedVisibleGamesCount = computed(() => {
    const cols = currentColumns.value

    if (!props.trimDanglingTiles || !cols || !props.games?.length) return visibleGamesCount.value

    return getLastCompleteRowEnd(cols, visibleGamesCount.value)
})

if (import.meta.client) {
    watch([() => props.games, trimmedVisibleGamesCount], async () => {
        await nextTick()

        observeTileSlots()

        if (impressions.enabled) observeImpressions(gameListRef.value)
    })
}
</script>

<template>
    <div>
        <div
            ref="gameList"
            class="game-list"
            :class="{
                'game-list--has-sidebar': props.hasSidebar,
            }"
            :style="{ '--max-columns': props.maxColumns }"
        >
            <div
                v-for="index in trimmedVisibleGamesCount"
                :key="props.games?.[index - 1]?.slug ?? `skeleton_${index}`"
                :data-tile-index="index - 1"
                class="game-list__tile-slot"
                :class="{
                    'game-list__tile-slot--video': isVideoTile(props.games?.[index - 1], props.videoEnabled),
                    'game-list__tile-slot--loaded': loadedTiles.has(index - 1),
                }"
                :style="{ '--game-index': getGameSetIndex(index - 1) }"
            >
                <GameTile
                    v-if="visibleTiles.has(index - 1) && props.games?.[index - 1]"
                    :game="(props.games[index - 1] as Game)"
                    :video-enabled="props.videoEnabled"
                    :eager="isEagerTile(index - 1)"
                    :load-media="mediaTiles.has(index - 1)"
                    @game-click="(game: Game) => emit('game-click', game, tilePosition(index - 1))"
                    @loaded="loadedTiles.add(index - 1)"
                />

                <Transition name="skeleton-fade">
                    <div v-if="!loadedTiles.has(index - 1)" class="skeleton-item game-list__tile-skeleton" />
                </Transition>
            </div>
        </div>

        <template v-if="currentGamesCount">
            <div
                v-if="props.showMoreButton"
                class="game-list__action game-list__action--load-more"
                :class="{ 'game-list--loading': props.loadingMore }"
            >
                <div v-if="gameListBarWidth > 0" class="game-list__states">
                    <div class="game-list__bar">
                        <span class="game-list__bar-progress" :style="`width: ${gameListBarWidth}%;`" />
                    </div>
                    <p>
                        {{ t('game.gameList.games_displayed', { gamesDisplayed: currentGamesCount, totalGames: props.totalGames ?? '' }) }}
                    </p>
                </div>
                <UIButton
                    class="game-list__load-more-button"
                    color="secondary"
                    :loading="props.loadingMore"
                    :disabled="currentGamesCount === props.totalGames || props.loadingMore"
                    @click.prevent="loadMoreGames(category)"
                >
                    {{ t('game.gameList.load_more') }}
                </UIButton>
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
$ctaMinWidth: 212px;

.game-list {
    &--has-sidebar {
        position: relative;
    }

    &__action--load-more {
        flex-direction: column;
    }

    &__states {
        position: relative;
        display: flex;
        text-align: center;
        align-items: center;
        justify-content: center;
        min-width: $ctaMinWidth;

        p {
            margin: 13px 0 10px;
        }
    }

    &__bar {
        width: 100%;
        background: $game-load-more-bar-bg; // Replace with variable in time

        &,
        &-progress {
            position: absolute;
            top: 0;
            left: 0;
            height: 3px;
        }

        &-progress {
            background: $game-load-more-progress-bg;
            transition: width 0.5s ease-out;
        }
    }

    &__load-more-button {
        min-height: 50px;

        &:disabled {
            display: none;
        }
    }

    &__tile-slot {
        position: relative;
        aspect-ratio: 1;

        &--video {
            grid-column: span 2;
            aspect-ratio: auto;
        }

        :deep(.game-tile .background) {
            opacity: 0;
            transition: opacity 0.2s ease-out;
        }

        &--loaded :deep(.game-tile .background) {
            opacity: 1;
        }
    }

    &__tile-skeleton {
        position: absolute;
        inset: 0;
        z-index: 2;
        aspect-ratio: auto;
    }
}

.skeleton-fade-leave-active {
    transition: opacity 0.3s ease-out;
}

.skeleton-fade-leave-to {
    opacity: 0;
}
</style>
